import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, KeyPoints } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'What Is Bitcoin?',
    tagline: 'Peer-to-peer electronic cash, and the problem it actually solves.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what the double-spend problem actually is
//   Q1 - who issues/controls bitcoin (no central issuer)
//   Q2 - what changed about who has to trust whom
export const blocks: StageBlock[] = [
    content('hero', () => {
        const colors = useColors();
        return (
            <StageSection gradientBorder eyebrow="Stage 1 of 8">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mb: 2 }}>
                    {['Peer-to-peer', 'No issuer', 'Fixed supply'].map((label) => (
                        <Chip
                            key={label}
                            label={label}
                            size="small"
                            variant="outlined"
                            sx={{ color: colors.accent.cyan, borderColor: colors.border.accent, backgroundColor: colors.accent.cyanGlow, fontFamily: '"Orbitron", sans-serif' }}
                        />
                    ))}
                </Box>
                <Typography variant="h2" sx={{
                    fontFamily: '"Orbitron", sans-serif',
                    fontSize: { xs: '1.9rem', md: '2.6rem' },
                    lineHeight: 1.1,
                    fontWeight: 800,
                    color: colors.text.primary,
                    mb: 2,
                }}>
                    Digital cash that doesn't need a bank in the middle.
                </Typography>
                <Prose>
                    In October 2008, someone using the name Satoshi Nakamoto published a short paper, "Bitcoin: A
                    Peer-to-Peer Electronic Cash System." The network described in it went live in January 2009.
                    Its goal was narrow and specific: let two people exchange digital money directly, without a bank,
                    payment processor, or any other trusted company sitting between them.
                </Prose>
                <Prose>
                    That sounds simple, but digital money has a problem physical cash never had.
                </Prose>
            </StageSection>
        );
    }),

    content('double-spend', () => (
        <StageSection title="The double-spend problem">
            <Prose>
                A digital file can be copied perfectly and instantly. If "money" were just a file, nothing would
                stop you from emailing the same file to two different people and having both of them believe they
                got paid. Before Bitcoin, the only fix was a trusted intermediary, a bank or company that kept the
                official ledger and simply refused to let you spend the same balance twice.
            </Prose>
            <Prose>
                Bitcoin's core innovation was making that ledger public and independently verifiable by everyone
                participating, so a network of mutually distrusting strangers could agree on which transactions
                happened first, without any single party being trusted to decide.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('no-central-issuer', () => (
        <StageSection title="Nobody issues it, and nobody can print more on a whim">
            <Prose>
                There is no company, bank, or government behind bitcoin. New coins are created as a reward paid to
                whoever successfully mines a new block, following a schedule fixed in the protocol itself: the
                reward halves at regular intervals, and total issuance is capped at 21 million bitcoin. Anyone can
                download node software and become part of the network that validates it; there's no application
                process and no permission to ask for.
            </Prose>
        </StageSection>
    )),

    question(1),

    content('trust-model', () => (
        <StageSection title="What actually changed">
            <Prose>
                Traditional digital payments still work by trusting a ledger-keeper: your bank's database says what
                your balance is, and you take its word for it. Bitcoin doesn't remove verification; it moves it.
                Every full node can check the entire history of the ledger itself and independently confirm that a
                given payment is valid, rather than trusting a single company's internal records.
            </Prose>
            <KeyPoints items={[
                <>Bitcoin doesn't eliminate trust; it replaces "trust one company's ledger" with "verify the shared ledger yourself."</>,
                <>The rest of this track builds up exactly how that shared ledger works: keys, transactions, blocks, and the process that keeps everyone's copy in agreement.</>,
            ]} />
        </StageSection>
    )),

    question(2),
];
